import React, { useState, useEffect, useMemo } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  Plus,
  Users,
  Pencil,
  Trash2,
  Loader2,
  UserPlus,
  LayoutTemplate,
} from "lucide-react";
import ApplyTemplateModal from "@/components/templates/ApplyTemplateModal";

const VENUE_TYPES = {
  restaurant: "Restaurant",
  bar: "Bar",
  cafeteria: "Cafeteria",
  takeaway: "Take Away",
  rostisseria: "Rostisseria",
  hotel_cuina: "Hotel amb Cuina",
  pastisseria: "Pastisseria / Forn",
  other: "Altres",
};

export default function AdminVenues() {
  const { user, userRole, loading: venueLoading, refreshData } = useVenue();
  const [venues, setVenues] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVenueForm, setShowVenueForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applyTemplateVenue, setApplyTemplateVenue] = useState(null);

  const [venueForm, setVenueForm] = useState({
    name: "", address: "", city: "", phone: "", email: "", venue_type: "restaurant",
  });
  const [memberForm, setMemberForm] = useState({
    user_email: "", user_name: "", role: "worker",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [v, m] = await Promise.all([
      hosys.entities.Venue.list(),
      hosys.entities.VenueMember.list(),
    ]);
    setVenues(v);
    setMembers(m);
    setLoading(false);
  };

  const handleSaveVenue = async () => {
    setSaving(true);
    if (editingVenue) {
      await hosys.entities.Venue.update(editingVenue.id, venueForm);
    } else {
      await hosys.entities.Venue.create({ ...venueForm, owner_email: user.email, active: true });
    }
    setShowVenueForm(false);
    setEditingVenue(null);
    await loadData();
    await refreshData();
    setSaving(false);
  };

  const handleDeleteVenue = async (venue) => {
    await hosys.entities.Venue.delete(venue.id);
    await loadData();
    await refreshData();
  };

  const handleAddMember = async () => {
    if (!memberForm.user_email || !selectedVenue) return;
    setSaving(true);
    await hosys.entities.VenueMember.create({
      venue_id: selectedVenue.id,
      user_email: memberForm.user_email,
      user_name: memberForm.user_name,
      role: memberForm.role,
      active: true,
    });
    setShowMemberForm(false);
    setMemberForm({ user_email: "", user_name: "", role: "worker" });
    await loadData();
    setSaving(false);
  };

  const handleRemoveMember = async (member) => {
    await hosys.entities.VenueMember.delete(member.id);
    await loadData();
  };

  const openEditVenue = (venue) => {
    setEditingVenue(venue);
    setVenueForm({
      name: venue.name,
      address: venue.address || "",
      city: venue.city || "",
      phone: venue.phone || "",
      email: venue.email || "",
      venue_type: venue.venue_type || "restaurant",
    });
    setShowVenueForm(true);
  };

  const openNewVenue = () => {
    setEditingVenue(null);
    setVenueForm({ name: "", address: "", city: "", phone: "", email: "", venue_type: "restaurant" });
    setShowVenueForm(true);
  };

  const handleSaveVenueWithTemplate = async () => {
    setSaving(true);
    if (editingVenue) {
      await hosys.entities.Venue.update(editingVenue.id, venueForm);
      setShowVenueForm(false);
      setEditingVenue(null);
      await loadData();
      await refreshData();
    } else {
      const created = await hosys.entities.Venue.create({ ...venueForm, owner_email: user.email, active: true });
      setShowVenueForm(false);
      setEditingVenue(null);
      await loadData();
      await refreshData();
      // Offer to apply template
      setApplyTemplateVenue({ ...venueForm, id: created.id });
    }
    setSaving(false);
  };

  const isSuperAdmin = userRole === "superadmin";
  const isAdmin = userRole === "admin";
  const filteredVenues = useMemo(() => 
    venues.filter(v => isSuperAdmin || v.owner_email === user?.email),
    [venues, isSuperAdmin, user?.email]
  );

  if (venueLoading || loading) return <QuickLoader />;

  if (!isSuperAdmin && !isAdmin) {
    return (
      <EmptyState
        icon={Building2}
        title="Accés restringit"
        description="Necessites permisos d'administrador per accedir a aquesta secció."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Administració de Locals"
        subtitle={`${venues.length} locals registrats`}
        actions={
          <Button onClick={openNewVenue} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl">
            <Plus className="w-4 h-4" /> Nou local
          </Button>
        }
      />

      {/* Venues list */}
      {/* Admins only see their own venues */}
      {venues.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Cap local creat"
          description="Crea el primer local per començar."
          action={
            <Button onClick={openNewVenue} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              <Plus className="w-4 h-4" /> Crear local
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {filteredVenues
            .map((venue) => {
            const venueMembers = members.filter(m => m.venue_id === venue.id);
            return (
              <Card key={venue.id} className="border-0 shadow-sm overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{venue.name}</h3>
                        <Badge variant="secondary" className="text-xs">{VENUE_TYPES[venue.venue_type]}</Badge>
                        {venue.active ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs border-0">Actiu</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Inactiu</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 space-y-0.5">
                        {venue.address && <p>{venue.address}{venue.city ? `, ${venue.city}` : ""}</p>}
                        {venue.phone && <p>Tel: {venue.phone}</p>}
                        {venue.email && <p>{venue.email}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" title="Aplicar plantilla" onClick={() => setApplyTemplateVenue(venue)}>
                        <LayoutTemplate className="w-4 h-4 text-[#7B2D3F]" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditVenue(venue)}>
                        <Pencil className="w-4 h-4 text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteVenue(venue)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>

                  {/* Members */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Equip ({venueMembers.length})
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#7B2D3F] gap-1"
                        onClick={() => { setSelectedVenue(venue); setShowMemberForm(true); }}
                      >
                        <UserPlus className="w-3 h-3" /> Afegir
                      </Button>
                    </div>
                    {venueMembers.length > 0 && (
                      <div className="bg-gray-50 rounded-xl overflow-x-auto">
                        <div className="min-w-full">
                          {venueMembers.map((m) => (
                            <div key={m.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{m.user_name || "—"}</p>
                                <p className="text-xs text-gray-500 truncate">{m.user_email}</p>
                              </div>
                              <Badge variant="outline" className="text-xs capitalize flex-shrink-0">{m.role}</Badge>
                              <Button variant="ghost" size="icon" className="flex-shrink-0 h-7 w-7" onClick={() => handleRemoveMember(m)}>
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Venue form dialog */}
      <Dialog open={showVenueForm} onOpenChange={setShowVenueForm}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
          <div className="overflow-y-auto flex-1">
            <div className="p-6 pb-2">
              <DialogHeader>
                <DialogTitle>{editingVenue ? "Editar Local" : "Nou Local"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Nom del local *</Label>
                  <Input value={venueForm.name} onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>Tipus</Label>
                  <Select value={venueForm.venue_type} onValueChange={(v) => setVenueForm({ ...venueForm, venue_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(VENUE_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Adreça</Label>
                  <Input value={venueForm.address} onChange={(e) => setVenueForm({ ...venueForm, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Ciutat</Label>
                    <Input value={venueForm.city} onChange={(e) => setVenueForm({ ...venueForm, city: e.target.value })} />
                  </div>
                  <div>
                    <Label>Telèfon</Label>
                    <Input value={venueForm.phone} onChange={(e) => setVenueForm({ ...venueForm, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={venueForm.email} onChange={(e) => setVenueForm({ ...venueForm, email: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowVenueForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSaveVenueWithTemplate} disabled={!venueForm.name || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingVenue ? "Actualitzar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply template modal */}
      <ApplyTemplateModal
        open={!!applyTemplateVenue}
        venue={applyTemplateVenue}
        onClose={() => setApplyTemplateVenue(null)}
      />

      {/* Member form dialog */}
      <Dialog open={showMemberForm} onOpenChange={setShowMemberForm}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
          <div className="overflow-y-auto flex-1">
            <div className="p-6 pb-2">
              <DialogHeader>
                <DialogTitle>Afegir membre a {selectedVenue?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Email de l'usuari *</Label>
                  <Input
                    type="email"
                    placeholder="email@exemple.com"
                    value={memberForm.user_email}
                    onChange={(e) => setMemberForm({ ...memberForm, user_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    placeholder="Nom complet"
                    value={memberForm.user_name}
                    onChange={(e) => setMemberForm({ ...memberForm, user_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Rol</Label>
                  <Select value={memberForm.role} onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="worker">Treballador</SelectItem>
                      <SelectItem value="auditor">Auditor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowMemberForm(false)}>Cancel·lar</Button>
            <Button onClick={handleAddMember} disabled={!memberForm.user_email || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Afegir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}